import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, Phone } from "lucide-react";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const YOUTH_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Description" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "phone", label: "Contact phone", placeholder: "(469) 343-4228" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface YouthEntry {
  name: string;
  description: string;
  website: string;
  address: string;
  phone?: string;
  image: string;
  hideViewLocation?: boolean;
  instagram?: string;
  bigInstagramButton?: boolean;
}

export const YOUTH_ENTRIES: YouthEntry[] = [
  {
    name: "Inspiration Masters",
    description: "Public Speaking & Leadership Programs",
    website: "",
    address: "Dallas, TX",
    phone: "972-974-7573",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-22/9d4cc0ca2340.jpeg",
    instagram: "https://www.instagram.com/p/DV2BqJBmnd9/",
    bigInstagramButton: true,
    hideViewLocation: true,
  },
  {
    name: "The Success Door",
    description: "Transformation Life Coach",
    website: "https://thesuccessdoor.com",
    address: "Dallas",
    phone: "469-988-4134",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-07/ab04b18e6371.jpeg",
    hideViewLocation: true,
  },
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&h=400&fit=crop";

function mapsUrl(e: YouthEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

export function YouthCard({ entry, index }: { entry: YouthEntry; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full h-[180px] relative overflow-hidden bg-gray-100 flex items-center justify-center">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-1.5 pb-0.5 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-[10px] font-bold text-[#082f6d] leading-tight truncate">
            {entry.name}
          </h3>
          {entry.description && (
            <p className="text-[9px] text-gray-500 font-medium leading-snug mt-0.5">
              {entry.description}
            </p>
          )}
          {entry.address && (
            <div className="flex items-start gap-0.5 mt-0.5">
              <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
                {entry.address}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        {/* Website icon omitted — entries with a website already get a
            dedicated "Visit Website" button below, so the round icon would
            duplicate that same link (e.g. The Success Door). */}
        <SocialLinks
          item={{ ...entry, website: undefined, instagram: entry.bigInstagramButton ? undefined : entry.instagram }}
          px={20}
        />
        {entry.bigInstagramButton && entry.instagram && (
          <a
            href={entry.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-1 bg-gradient-to-br from-pink-500 to-orange-400 hover:opacity-90 text-white text-[9px] font-semibold py-1.5 rounded-sm transition-opacity duration-200 cursor-pointer"
          >
            <svg style={{ width: 11, height: 11 }} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
            </svg>
            Instagram
          </a>
        )}
        {entry.website && (
          <form action={entry.website} method="get" target="_top" className="w-full">
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-0.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
            >
              <ExternalLink className="w-2.5 h-2.5" /> Visit Website
            </button>
          </form>
        )}
        {!entry.website && entry.phone && (
          <a
            href={`tel:${entry.phone.replace(/[^\d+]/g, "")}`}
            className="w-full flex items-center justify-center gap-0.5 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
          >
            <Phone className="w-2.5 h-2.5" /> Call
          </a>
        )}
        {entry.address && !entry.hideViewLocation && (
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-0.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <ExternalLink className="w-2.5 h-2.5" /> View Location
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Youth() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("youth", YOUTH_ENTRIES, (e, i) => slugify(e.name || String(i)));

  const filtered = mc.items.filter(
    (e) =>
      query.trim() === "" ||
      e.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Youth"
        placeholder="Search youth programs & organizations…"
        backgroundImage="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1440&h=400&fit=crop"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Youth Programs & Organizations
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Find youth programs, activities, and organizations in the Dallas-DFW area.
            </p>
            <AdminAddBar mc={mc} fields={YOUTH_FIELDS} label="Add Listing" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={YOUTH_FIELDS} title="Listing">
                    <YouthCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No youth listings yet.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
